export type AssetKind = "gltf" | "pbr";
export type TextureMapKind = "baseColor" | "normal" | "roughness" | "ao";

export interface AssetFile {
  url: string;
  bytes: number;
  sha256: string;
  estimatedGpuBytes?: number;
}

export interface AssetLod extends AssetFile {
  level: number;
  minDistance: number;
  maxDistance: number;
}

export interface AssetVariant {
  key: string;
  lods: AssetLod[];
}

interface AssetDefinitionBase {
  id: string;
  kind: AssetKind;
  core: boolean;
  bytes: number;
  estimatedGpuBytes: number;
  licenseId: string;
}

export interface GltfAssetDefinition extends AssetDefinitionBase {
  kind: "gltf";
  url: string;
  sha256: string;
  dependencies?: AssetFile[];
  variants?: AssetVariant[];
}

export interface PbrAssetDefinition extends AssetDefinitionBase {
  kind: "pbr";
  maps: Partial<Record<TextureMapKind, AssetFile>>;
  repeat?: [number, number];
}

export type AssetDefinition = GltfAssetDefinition | PbrAssetDefinition;

export interface AssetManifest {
  assetPackVersion: string;
  coreBudgetBytes: number;
  coreGpuBudgetBytes: number;
  assets: AssetDefinition[];
}

export interface AssetLicense {
  id: string;
  name: string;
  author: string;
  sourceUrl: string;
  license: string;
  licenseUrl: string;
  modifications: string;
}

export interface AssetLicenseLedger {
  licenses: AssetLicense[];
}

export async function loadAssetManifest(
  baseUrl = import.meta.env.BASE_URL,
  fetcher: typeof fetch = fetch,
): Promise<AssetManifest> {
  return fetchJson<AssetManifest>(resolveAssetUrl(baseUrl, "assets/v1/manifest.json"), fetcher);
}

export async function loadAssetLicenses(
  baseUrl = import.meta.env.BASE_URL,
  fetcher: typeof fetch = fetch,
): Promise<AssetLicenseLedger> {
  return fetchJson<AssetLicenseLedger>(resolveAssetUrl(baseUrl, "assets/v1/licenses.json"), fetcher);
}

export function findAsset(manifest: AssetManifest, id: string): AssetDefinition {
  const asset = manifest.assets.find((candidate) => candidate.id === id);
  if (!asset) throw new Error(`Unknown asset: ${id}`);
  return asset;
}

export function selectVariant(asset: GltfAssetDefinition, preferredKey?: string): AssetVariant | undefined {
  if (!asset.variants?.length) return undefined;
  const variants = [...asset.variants].sort((left, right) => left.key.localeCompare(right.key));
  return variants.find((variant) => variant.key === preferredKey) ?? variants[0];
}

export function selectLod(asset: GltfAssetDefinition, distance = 0, preferredKey?: string): AssetFile {
  const variant = selectVariant(asset, preferredKey);
  if (!variant) return asset;
  const lods = [...variant.lods].sort((left, right) => left.level - right.level);
  return lods.find((lod) => distance >= lod.minDistance && distance < lod.maxDistance) ?? lods.at(-1) ?? asset;
}

export function resolveAssetUrl(baseUrl: string, relativeUrl: string): string {
  if (/^(?:[a-z]+:)?\/\//i.test(relativeUrl) || relativeUrl.startsWith("/")) {
    throw new Error(`Asset URL must be relative to BASE_URL: ${relativeUrl}`);
  }
  const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return `${base}${relativeUrl.replace(/^\.\//, "")}`;
}

async function fetchJson<T>(url: string, fetcher: typeof fetch): Promise<T> {
  const response = await fetcher(url);
  if (!response.ok) throw new Error(`Failed to load ${url}: HTTP ${response.status}`);
  return response.json() as Promise<T>;
}
